// A very small promise wrapper over IndexedDB, plus a memory fallback.
//
// Why not localStorage, which is what the cart store uses: this holds a whole
// product catalog and, more importantly, sales that have been paid for but not yet
// pushed. localStorage caps at roughly 5MB per origin, is synchronous (so a large
// write janks the till at exactly the wrong moment), and throws QuotaExceededError
// with nothing useful to do about it. Losing a cart to that is annoying; losing a
// sale is money.
//
// Why not a library: two object stores and five operations. `idb` would be a
// dependency in a bundle that deliberately carries no credentials and very little
// else.
//
// EVERY operation degrades to an in-memory Map rather than throwing. A private
// window, a browser set to block site data, or a corrupt database must leave the
// till working exactly as it did before any of this existed — slow, but working.

const DB_NAME = 'tendally'
const DB_VERSION = 1

/** Catalog documents and cursors, keyed by a namespaced string. */
export const KV = 'kv'
/** Pending sales, keyed by their client-generated sale id. */
export const QUEUE = 'queue'

export type StoreName = typeof KV | typeof QUEUE

interface QueueRecord {
  id: string
}

let dbPromise: Promise<IDBDatabase | null> | null = null

/** The fallback. One Map per object store, shared for the life of the tab. */
const memory: Record<StoreName, Map<string, unknown>> = {
  [KV]: new Map(),
  [QUEUE]: new Map()
}

let warned = false
function fallback(err: unknown): null {
  if (!warned) {
    warned = true
    console.warn('[web] IndexedDB unavailable — catalog cache and sale queue are memory-only', err)
  }
  return null
}

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    let req: IDBOpenDBRequest
    try {
      // Merely TOUCHING indexedDB throws in some configurations (blocked site data,
      // certain private modes), so even the property access is guarded.
      if (typeof indexedDB === 'undefined') return resolve(fallback('no indexedDB'))
      req = indexedDB.open(DB_NAME, DB_VERSION)
    } catch (err) {
      return resolve(fallback(err))
    }
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(KV)) db.createObjectStore(KV)
      // In-line key: a queue record carries its own sale id, which is also the
      // idempotency key the server dedupes on.
      if (!db.objectStoreNames.contains(QUEUE)) db.createObjectStore(QUEUE, { keyPath: 'id' })
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => resolve(fallback(req.error))
    // Another tab holds an older version open. Rather than hang forever waiting for
    // it to close, fall back for this tab.
    req.onblocked = () => resolve(fallback('blocked by another tab'))
  })
  return dbPromise
}

function run<T>(
  store: StoreName,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>
): Promise<T | undefined> {
  return openDb().then(
    (db) =>
      new Promise<T | undefined>((resolve) => {
        if (!db) return resolve(undefined)
        let req: IDBRequest<T>
        try {
          req = fn(db.transaction(store, mode).objectStore(store))
        } catch (err) {
          fallback(err)
          return resolve(undefined)
        }
        req.onsuccess = () => resolve(req.result)
        // A failed read or write is reported as "nothing there", never as a throw.
        // Callers all have a correct answer for that: refetch from the server.
        req.onerror = () => {
          fallback(req.error)
          resolve(undefined)
        }
      })
  )
}

async function usingMemory(): Promise<boolean> {
  return (await openDb()) === null
}

export async function kvGet<T>(key: string): Promise<T | undefined> {
  if (await usingMemory()) return memory[KV].get(key) as T | undefined
  return (await run<T>(KV, 'readonly', (s) => s.get(key) as IDBRequest<T>)) ?? undefined
}

export async function kvPut(key: string, value: unknown): Promise<void> {
  if (await usingMemory()) {
    memory[KV].set(key, value)
    return
  }
  await run(KV, 'readwrite', (s) => s.put(value, key))
}

export async function kvDelete(key: string): Promise<void> {
  if (await usingMemory()) {
    memory[KV].delete(key)
    return
  }
  await run(KV, 'readwrite', (s) => s.delete(key))
}

export async function queueAll<T extends QueueRecord>(): Promise<T[]> {
  if (await usingMemory()) return [...memory[QUEUE].values()] as T[]
  return (await run<T[]>(QUEUE, 'readonly', (s) => s.getAll() as IDBRequest<T[]>)) ?? []
}

export async function queuePut<T extends QueueRecord>(record: T): Promise<void> {
  if (await usingMemory()) {
    memory[QUEUE].set(record.id, record)
    return
  }
  await run(QUEUE, 'readwrite', (s) => s.put(record))
}

export async function queueDelete(id: string): Promise<void> {
  if (await usingMemory()) {
    memory[QUEUE].delete(id)
    return
  }
  await run(QUEUE, 'readwrite', (s) => s.delete(id))
}

/** Test seam: forget the connection so the next call re-opens (or re-falls-back). */
export function _resetIdb(): void {
  dbPromise = null
  memory[KV].clear()
  memory[QUEUE].clear()
  warned = false
}
